import { useMemo } from 'react'
import { Connection } from 'features/connections'
import { useMutation } from 'react-query'
import { useIntl } from 'react-intl'
import { useInvalidateWorkflowsList } from 'features/workflows/hooks/query'
import { WorkflowPayload } from '../types'
import { showSelectConnectionDialog } from '../ui/Dialogs'
import { useConnections } from 'features/connections/hooks/connectionsHooks'
import { workflowClient } from '../service/clients/WorkflowClient'
import { DuplicateWorkflowModel } from '../service/clients/WorkflowClient/types'

export function useDuplicateWorkflow(workflow?: WorkflowPayload) {
  const intl = useIntl()
  const { invalidateWorkflowsList } = useInvalidateWorkflowsList()
  const { mutateAsync, isLoading } = useMutation((values: DuplicateWorkflowModel) => workflowClient.duplicate(values))
  const { data: connections } = useConnections()

  const availableConnections = useMemo(() => {
    // TODO: REFACTOR: list api has connectionProvider on the workflow root object. Get API has it on the config
    // I think we should:
    // 1. remove connectionProvider from the workflow (we already have connection_id)
    // 2. Have a new API endpoint /connection/<connection_id>/type which returns eg {"type": "snowflake"}
    // This is only needed in situations where the user duplicating the workflow doesn't have access to the original connection
    // used to make the workflow
    return (
      connections?.filter(
        (conn) => conn.provider_id === ((workflow?.connectionProvider || workflow?.config.connectionProvider) as string)
      ) || []
    )
  }, [connections, workflow?.connectionProvider, workflow?.config?.connectionProvider])

  const selectConnectionAndDuplicate = async (connection: Connection) => {
    const { id } = await mutateAsync({ workflowId: workflow?.id as string, connectionId: connection.id })
    invalidateWorkflowsList()

    window.open(`/workflows/${id}`, '_blank', 'noopener,noreferrer')
  }

  return {
    isLoading,
    async duplicate() {
      const connection = connections?.find((conn) => conn.id === workflow?.connectionId)

      if (connection) {
        return selectConnectionAndDuplicate(connection)
      }

      showSelectConnectionDialog({
        title: intl.formatMessage({ id: 'workflows.duplicate.title' }),
        description: intl.formatMessage({ id: 'workflows.duplicate.description' }),
        connections: availableConnections || [],
        onSelect: selectConnectionAndDuplicate
      })
    }
  }
}
